<template>
  <div>
    <b-card class="mb-3">
      <p class="mb-3">
        <i class="icon-exclamation text-info text-lg"></i>
        {{ $t('vehicles.attributes.pricing.new_layout_route_price_notify') }}
      </p>
      <div class="form-row">
        <b-form-group
          class="col-md-4"
          :label="$t('vehicles.attributes.pricing.night_threshold_start.label')"
        >
          <night-threshold-time-input
            v-model="tabsData[pricingKey].night_threshold.start"
            :input-id="`night-start-${pricingKey}`"
            :placeholder="
              $t('vehicles.attributes.pricing.night_threshold_start.placeholder')
            "
          />
        </b-form-group>
        <b-form-group
          class="col-md-4"
          :label="$t('vehicles.attributes.pricing.night_threshold_end.label')"
        >
          <night-threshold-time-input
            v-model="tabsData[pricingKey].night_threshold.end"
            :input-id="`night-end-${pricingKey}`"
            :placeholder="
              $t('vehicles.attributes.pricing.night_threshold_end.placeholder')
            "
          />
        </b-form-group>
        <b-form-group
          class="col-md-4"
          :label="$t('vehicles.attributes.pricing.days_metric.label')"
        >
          <b-form-select
            v-model="tabsData[pricingKey].days_metric"
            :options="daysMetricOptions"
          />
        </b-form-group>
      </div>
    </b-card>

    <div class="table-responsive-wide">
      <table class="table table-sm table-bordered day-night-grid">
        <thead>
          <tr>
            <th class="vehicle-col">
              {{ $t('vehicles.name') }}
            </th>
            <th v-for="(day, i) in days" :key="day" class="day-col text-center">
              {{ weekdayTitle(i) }}
              <div class="small text-muted font-weight-normal">
                {{ $t('general.day') }} / {{ $t('general.night') }}
              </div>
            </th>
            <th class="row-clear-col text-center align-middle" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in vehicles" :key="v.id">
            <td class="align-middle vehicle-col">
              <router-link v-if="$allows('vehicles.index')" :to="{ name: 'vehicles.edit', params: { id: v.id } }">
                {{ v.name }}
              </router-link>
              <span v-else>{{ v.name }}</span>
            </td>
            <td v-for="day in days" :key="`${v.id}-${day}-${pricingKey}`" class="day-cell">
              <div class="input-group input-group-sm mb-1">
                <div v-if="showCurrencyPrefix(pricingKey)" class="input-group-prepend">
                  <span class="input-group-text">{{selectedCurrency.symbol}}</span>
                </div>
                <b-form-input
                  v-model="tabsData[pricingKey].vehicleCells[v.id][day].day"
                  type="number"
                  step="0.01"
                  size="sm"
                  :placeholder="$t('general.day')"
                />
              </div>
              <div class="input-group input-group-sm">
                <div v-if="showCurrencyPrefix(pricingKey)" class="input-group-prepend">
                  <span class="input-group-text">{{selectedCurrency.symbol}}</span>
                </div>
                <b-form-input
                  v-model="tabsData[pricingKey].vehicleCells[v.id][day].night"
                  type="number"
                  step="0.01"
                  size="sm"
                  :placeholder="$t('general.night')"
                />
              </div>
            </td>
            <td class="text-center align-middle row-clear-col">
              <button
                type="button"
                class="btn btn-link p-1 day-night-row-clear"
                :title="$t('vehicles.day_night_clear_row')"
                :aria-label="$t('vehicles.day_night_clear_row')"
                @click="clearVehicleRow(v.id)"
              >
                <i class="icon-trash day-night-row-clear__icon" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-3">
      <b-button
        v-if="$allows('vehicles.update')"
        variant="primary"
        :disabled="saving"
        @click="$emit('save', pricingKey)"
      >
        {{ $t('general.save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import NightThresholdTimeInput from './NightThresholdTimeInput.vue'

export default {
  name: 'DayNightTabContent',

  components: {
    NightThresholdTimeInput,
  },

  props: {
    pricingKey: {
      type: String,
      required: true,
    },
    tabsData: {
      type: Object,
      required: true,
    },
    vehicles: {
      type: Array,
      required: true,
    },
    days: {
      type: Array,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    daysMetricOptions: {
      type: Array,
      required: true,
    },
    selectedCurrency: {
      type: Object,
      required: true,
    },
  },

  methods: {
    weekdayTitle(index) {
      return this.$moment()
        .day(index + 1)
        .format('dddd')
    },
    showCurrencyPrefix(pricingKey) {
      const m = this.tabsData[pricingKey].days_metric
      return m === 'fixed' || m === 'surcharge'
    },

    clearVehicleRow(vehicleId) {
      const cellsRoot = this.tabsData[this.pricingKey].vehicleCells
      const cells = cellsRoot[vehicleId]
      if (!cells) {
        return
      }
      for (const day of this.days) {
        if (!cells[day]) {
          continue
        }
        this.$set(cells[day], 'day', '')
        this.$set(cells[day], 'night', '')
      }
    },
  },
}
</script>

<style scoped>
.table-responsive-wide {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.day-night-grid .vehicle-col {
  min-width: 10rem;
  white-space: nowrap;
}
.day-night-grid .day-col {
  min-width: 7.5rem;
  vertical-align: middle;
}
.day-night-grid .day-cell {
  vertical-align: middle;
}
.day-night-grid .input-group-text {
  min-width: 2rem;
  justify-content: center;
}
.row-clear-col {
  width: 2.75rem;
  vertical-align: middle;
}
.day-night-row-clear {
  line-height: 1;
}
.day-night-row-clear:hover .day-night-row-clear__icon,
.day-night-row-clear:focus .day-night-row-clear__icon {
  color: #c62828 !important;
}
.day-night-row-clear__icon {
  font-size: 1.1rem;
  color: #dc3545;
}
</style>
